import { ReactNode } from 'react';
import { Box, FormControlLabel, Radio, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { riseIn } from 'components/ui/styleKit';
import { RADIUS, REDUCED_MOTION } from 'components/ui/tokens';

type Props = {
  value: string;
  icon: ReactNode;
  label: string;
  /** A second, quieter line for a choice that needs explaining. */
  description?: string;
  checked: boolean;
  /** Position in its list, for the staggered entrance. */
  index: number;
  disabled?: boolean;
};

/** One answer of a radio list in the dialog: an icon tile, the label, and the radio on the right. */
export default function ChoiceRow({ value, icon, label, description, checked, index, disabled }: Props) {
  return (
    <FormControlLabel
      value={value}
      disabled={disabled}
      labelPlacement="start"
      control={<Radio size="small" />}
      label={
        <Stack direction="row" spacing={1.5} alignItems={description ? 'flex-start' : 'center'}>
          <Box
            aria-hidden
            sx={(theme) => ({
              width: 28,
              height: 28,
              flexShrink: 0,
              borderRadius: 1,
              display: 'grid',
              placeItems: 'center',
              fontSize: 14,
              color: checked ? 'primary.main' : 'text.secondary',
              bgcolor: checked ? alpha(theme.palette.primary.main, 0.12) : 'action.hover',
              transition: 'background-color 150ms, color 150ms'
            })}
          >
            {icon}
          </Box>
          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="body2" sx={{ fontWeight: checked ? 500 : 400 }}>
              {label}
            </Typography>
            {description && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {description}
              </Typography>
            )}
          </Box>
        </Stack>
      }
      sx={(theme) => ({
        ...riseIn(index, { step: 40, distance: 4 }),
        m: 0,
        pl: 1.25,
        pr: 0.25,
        py: description ? 1 : 0.5,
        justifyContent: 'space-between',
        borderRadius: RADIUS.tile,
        border: '1px solid',
        borderColor: checked ? 'primary.main' : 'divider',
        bgcolor: checked ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
        transition: 'border-color 150ms, background-color 150ms, transform 150ms',
        '&:hover': {
          borderColor: checked ? 'primary.main' : 'primary.light',
          bgcolor: checked ? undefined : 'action.hover',
          transform: 'translateX(2px)'
        },
        [REDUCED_MOTION]: { animation: 'none', '&:hover': { transform: 'none' } }
      })}
    />
  );
}
